/** Bilingual name/id resolution for clients, sales reps, and users on write paths. */
type Row = Record<string, unknown>;

/** Client row bilingual pairs (DB English ← legacy Spanish). */
export const CLIENT_BILINGUAL_PAIRS: [string, string][] = [
  ['name', 'nombre'],
  ['phone', 'telefono'],
  ['country', 'pais'],
  ['address', 'direccion'],
  ['city', 'ciudad'],
  ['postalCode', 'codigoPostal'],
  ['clientType', 'tipoCliente'],
  ['legalPersonType', 'tipoPersona'],
  ['documentType', 'tipoDocumento'],
  ['isCredit', 'esCredito'],
  ['creditDays', 'diasPlazoCredito'],
  ['creditLimit', 'cupoCredito'],
  ['applyWithholdingTax', 'aplicaRetencionFuente'],
  ['withholdingTaxPct', 'pctRetencionFuente'],
  ['applyIca', 'aplicaIca'],
  ['icaPct', 'pctIca'],
];

/** Sales rep row bilingual pairs (DB English ← legacy Spanish). */
export const SALES_REP_BILINGUAL_PAIRS: [string, string][] = [
  ['name', 'nombre'],
  ['phone', 'telefono'],
  ['commission', 'comision'],
  ['zone', 'zona'],
  ['idNumber', 'cedula'],
  ['bankAccount', 'cuentaBancaria'],
  ['bankName', 'bancoNombre'],
  ['accountType', 'tipoCuenta'],
];

export function resolvePersonName(body: Row): string {
  return String(body.name ?? body.nombre ?? '').trim();
}

export function normalizeClientBody(body: Row): Row {
  const name = resolvePersonName(body);
  const phone = body.phone ?? body.telefono;
  const country = body.country ?? body.pais;
  const address = body.address ?? body.direccion;
  const city = body.city ?? body.ciudad;
  const postalCode = body.postalCode ?? body.codigoPostal;
  const clientType = body.clientType ?? body.tipoCliente;
  const legalPersonType = body.legalPersonType ?? body.tipoPersona;
  const documentType = body.documentType ?? body.tipoDocumento;
  const isCredit = body.isCredit ?? body.esCredito;
  const creditDays = body.creditDays ?? body.diasPlazoCredito;
  const creditLimit = body.creditLimit ?? body.cupoCredito;
  const applyWithholdingTax = body.applyWithholdingTax ?? body.aplicaRetencionFuente;
  const withholdingTaxPct = body.withholdingTaxPct ?? body.pctRetencionFuente;
  const applyIca = body.applyIca ?? body.aplicaIca;
  const icaPct = body.icaPct ?? body.pctIca;

  return {
    ...body,
    name,
    nombre: name,
    phone,
    telefono: phone,
    country,
    pais: country,
    address,
    direccion: address,
    city,
    ciudad: city,
    postalCode,
    codigoPostal: postalCode,
    clientType,
    tipoCliente: clientType,
    legalPersonType,
    tipoPersona: legalPersonType,
    documentType,
    tipoDocumento: documentType,
    isCredit,
    esCredito: isCredit,
    creditDays,
    diasPlazoCredito: creditDays,
    creditLimit,
    cupoCredito: creditLimit,
    applyWithholdingTax,
    aplicaRetencionFuente: applyWithholdingTax,
    withholdingTaxPct,
    pctRetencionFuente: withholdingTaxPct,
    applyIca,
    aplicaIca: applyIca,
    icaPct,
    pctIca: icaPct,
  };
}

export function normalizeSalesRepBody(body: Row): Row {
  const name = resolvePersonName(body);
  const phone = body.phone ?? body.telefono;
  const commission = body.commission ?? body.comision;
  const zone = body.zone ?? body.zona;
  const idNumber = body.idNumber ?? body.cedula;
  const bankAccount = body.bankAccount ?? body.cuentaBancaria;
  const bankName = body.bankName ?? body.bancoNombre;
  const accountType = body.accountType ?? body.tipoCuenta;

  return {
    ...body,
    name,
    nombre: name,
    phone,
    telefono: phone,
    commission,
    comision: commission,
    zone,
    zona: zone,
    idNumber,
    cedula: idNumber,
    bankAccount,
    cuentaBancaria: bankAccount,
    bankName,
    bancoNombre: bankName,
    accountType,
    tipoCuenta: accountType,
  };
}

export function resolveClientId(body: Row): string | undefined {
  const id = body.clientId ?? body.clienteId;
  return id != null ? String(id) : undefined;
}

export function resolveClientDisplayName(row: { name?: string; nombre?: string; empresa?: string } | null | undefined): string {
  if (!row) return '';
  return String(row.empresa || row.name || row.nombre || '').trim();
}

/** Keep name/nombre in sync when a form edits the display name field. */
export function withSyncedPersonName<T extends Row>(body: T, name: string): T {
  return { ...body, name, nombre: name };
}
